import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { BLOCK_MAIN, PLAN_BLOCKS } from '../lib/planEditing';
import { newWarmupSet, warmupWeight } from '../lib/warmupSets';
import { ArrowDown, ArrowUp, Info, Link2, Plus, RefreshCw, Trash2, Unlink2, X } from 'lucide-react';

const Field = ({ label, children }) => (
  <div>
    <Label className="label-text text-[#A1A1A1] mb-2 block text-[0.65rem]">{label}</Label>
    {children}
  </div>
);

const inputClass = 'bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white h-10';
const toInt = (v) => (v === '' ? null : parseInt(v, 10));

// Warm-up sets of this exercise: done before the working sets, saved inside the exercise.
const WarmupSets = ({ ex, onChange }) => {
  const list = ex.warmup_sets || [];
  const update = (i, patch) => onChange(list.map((w, j) => (j === i ? { ...w, ...patch } : w)));
  const small = 'bg-[#0A0A0A] border-[#2A2A2A] rounded-xl text-white h-9 text-sm';
  return (
    <div className="mt-4" data-testid="warmup-sets">
      <Label className="label-text text-[#A1A1A1] mb-2 block text-[0.65rem]">SERIE ROZGRZEWKOWE (przed seriami roboczymi, nie liczą się do objętości)</Label>
      {list.map((w, i) => {
        const auto = w.weight_kg == null ? warmupWeight(w, ex.weight_kg) : null;
        return (
          <div key={i} className="grid grid-cols-[64px_1fr_1fr_1fr_32px] gap-2 items-center mb-2">
            <span className="text-xs text-[#C9A84C]">Rozgrz. {i + 1}</span>
            <Input value={w.reps ?? ''} onChange={(e) => update(i, { reps: e.target.value || null })} placeholder="powt." aria-label={`Rozgrzewkowa ${i + 1} powtórzenia`} className={small} />
            <Input type="number" min="1" max="100" value={w.percent ?? ''} onChange={(e) => update(i, { percent: toInt(e.target.value) })} placeholder="% ciężaru" aria-label={`Rozgrzewkowa ${i + 1} procent`} className={small} />
            <Input type="number" step="0.5" value={w.weight_kg ?? ''} onChange={(e) => update(i, { weight_kg: parseFloat(e.target.value) || null })}
              placeholder={auto != null ? `${auto} kg` : 'kg'} aria-label={`Rozgrzewkowa ${i + 1} ciężar`} className={small} />
            <button onClick={() => onChange(list.filter((_, j) => j !== i))} className="text-[#A1A1A1] hover:text-red-400" aria-label={`Usuń rozgrzewkową ${i + 1}`}>
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
      <Button onClick={() => onChange([...list, newWarmupSet()])} variant="ghost" size="sm" className="text-[#C9A84C] hover:text-[#C9A84C] px-2" data-testid="add-warmup-set">
        <Plus className="w-4 h-4 mr-1.5" /> Dodaj serię rozgrzewkową
      </Button>
    </div>
  );
};

// Between two exercises of the main part: connects them into a superset or cuts it.
export const SupersetLink = ({ linked, onToggle, testId }) => (
  <button
    onClick={onToggle}
    className={`mx-auto flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition-all ${
      linked ? 'border-[#C9A84C] text-[#C9A84C]' : 'border-[#2A2A2A] text-[#A1A1A1] hover:border-[#C9A84C]/50'
    }`}
    data-testid={testId}
  >
    {linked ? <Unlink2 className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
    {linked ? 'Rozłącz super-serię' : 'Połącz w super-serię'}
  </button>
);

export const PlanExerciseCard = ({ ex, index, total, onChange, onBlock, onMove, onSwap, onRemove, onPreview }) => {
  const block = ex.circuit_group || BLOCK_MAIN;
  const blocks = PLAN_BLOCKS.some(([value]) => value === block) ? PLAN_BLOCKS : [...PLAN_BLOCKS, [block, block]];
  const iconButton = 'text-[#A1A1A1] hover:text-[#C9A84C] px-2 disabled:opacity-30';

  return (
    <div className={`card-surface rounded-3xl p-5 sm:p-6 ${ex.superset_group ? 'border border-[#C9A84C]/50' : ''}`} data-testid={`plan-exercise-${index}`}>
      <div className="mb-4">
        <span className="label-text text-[#C9A84C] text-xs mb-2 flex flex-wrap items-center gap-2">
          {index + 1}.
          {ex.circuit_group && <span className="px-2 py-0.5 rounded-full bg-[#C9A84C]/10 normal-case">{ex.circuit_group}</span>}
          {ex.superset_group && <span className="px-2 py-0.5 rounded-full bg-[#C9A84C] text-[#0A0A0A] normal-case">Super-seria {ex.superset_group}</span>}
        </span>
        <h3 className="heading-text text-xl text-white break-words">{ex.exercise_name}</h3>
        <div className="flex items-center gap-1 mt-3 -ml-2">
          <Button onClick={() => onMove(-1)} disabled={index === 0} variant="ghost" size="sm" className={iconButton}
            aria-label="Przesuń wyżej" title="Przesuń wyżej" data-testid={`move-up-${index}`}>
            <ArrowUp className="w-4 h-4" />
          </Button>
          <Button onClick={() => onMove(1)} disabled={index === total - 1} variant="ghost" size="sm" className={iconButton}
            aria-label="Przesuń niżej" title="Przesuń niżej" data-testid={`move-down-${index}`}>
            <ArrowDown className="w-4 h-4" />
          </Button>
          <Button onClick={onSwap} variant="ghost" size="sm" className="text-[#A1A1A1] hover:text-[#C9A84C] px-2" data-testid={`swap-${index}`}>
            <RefreshCw className="w-4 h-4 mr-1.5" /> Zamień
          </Button>
          <Button onClick={onPreview} variant="ghost" size="sm" className={`${iconButton} ml-auto`} aria-label="Zobacz instrukcję" title="Zobacz instrukcję">
            <Info className="w-4 h-4" />
          </Button>
          <Button onClick={onRemove} variant="ghost" size="sm" className="text-[#A1A1A1] hover:text-red-400 px-2" aria-label="Usuń ćwiczenie" title="Usuń ćwiczenie">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Field label="SERIE ROBOCZE">
          <Input type="number" min="1" value={ex.sets ?? ''} onChange={(e) => onChange('sets', toInt(e.target.value))} className={inputClass} />
        </Field>
        <Field label="POWTÓRZENIA">
          <Input value={ex.reps ?? ''} onChange={(e) => onChange('reps', e.target.value)} placeholder="10 lub 60 s" className={inputClass} />
        </Field>
        <Field label="TEMPO">
          <Input value={ex.tempo ?? ''} onChange={(e) => onChange('tempo', e.target.value || null)} placeholder="2-0-2-0" className={inputClass} />
        </Field>
        <Field label="RIR">
          <Input type="number" min="0" max="5" value={ex.rir ?? ''} onChange={(e) => onChange('rir', toInt(e.target.value))} className={inputClass} />
        </Field>
        <Field label="RPE">
          <Input type="number" min="1" max="10" value={ex.rpe ?? ''} onChange={(e) => onChange('rpe', toInt(e.target.value))} className={inputClass} />
        </Field>
        <Field label="PRZERWA (S)">
          <Input type="number" min="0" value={ex.rest_seconds ?? ''} onChange={(e) => onChange('rest_seconds', toInt(e.target.value))} className={inputClass} />
        </Field>
        <Field label="CIĘŻAR (KG)">
          <Input type="number" step="0.5" value={ex.weight_kg || ''} onChange={(e) => onChange('weight_kg', parseFloat(e.target.value) || null)} className={inputClass} />
        </Field>
        <Field label="CZĘŚĆ">
          <Select value={block} onValueChange={onBlock}>
            <SelectTrigger className={inputClass} data-testid={`block-${index}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {blocks.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>

      {block === BLOCK_MAIN && <WarmupSets ex={ex} onChange={(list) => onChange('warmup_sets', list)} />}

      <div className="mt-4">
        <Label className="label-text text-[#A1A1A1] mb-2 block text-[0.65rem]">NOTATKA DLA KLIENTA (widoczna w jego panelu)</Label>
        <Textarea
          value={ex.notes || ''}
          onChange={(e) => onChange('notes', e.target.value)}
          placeholder="Np. na co zwrócić uwagę, zamiennik przy bólu, indywidualna korekta techniki..."
          className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white text-sm"
          rows={2}
        />
      </div>
    </div>
  );
};
